'use client';

import { useRef, type CSSProperties, type ReactNode } from 'react';
import Link from 'next/link';
import { IconArrowDown, IconArrowRight, IconBolt, IconCpu, IconWorld } from '@tabler/icons-react';
import { formatCurrency } from '@/lib/utils/currency';
import { Button } from '@/components/ui/button';
import { machineCreateHref } from '@/lib/catalog/filters';
import { GpuCharacter, personaFor } from '@/components/shop/homepage/gpu-characters';
import { cn } from '@/lib/utils';
import { CYAN } from './arcade-classes';
import { GpuChip, Platform, RisingPixels, usePointerParallax, type RisingPixel } from './arcade-art';

interface GPUHeroSectionProps {
  totalGPUs: number;
  totalRegions: number;
  /** Lowest rentable base rate (the site-wide "from" price). */
  lowestPrice: number;
}

// The arcade crew, from the homepage character select.
const titan = personaFor('nvidia-a10g', 3);
const volt = personaFor('tesla-t4', 1);
const nova = personaFor('nvidia-l4', 2);
const oracle = personaFor('nvidia-l4-pro', 4);

const PIXELS: RisingPixel[] = [
  [8, 6, 0.4, 5, 'var(--primary)'],
  [19, 5, 2.2, 6, CYAN],
  [31, 7, 3.6, 4.6, 'var(--accent)'],
  [44, 5, 1.1, 5.4, 'var(--chart-4)'],
  [57, 8, 4.4, 6.2, 'var(--primary)'],
  [66, 5, 0.8, 4.2, 'var(--chart-5)'],
  [78, 6, 2.9, 5.8, CYAN],
  [88, 7, 1.7, 4.8, 'var(--accent)'],
  [95, 5, 3.3, 6.4, 'var(--primary)'],
];

/** A parallax layer: moves with the cursor by --d px. */
function Layer({ depth, className, children }: { depth: number; className?: string; children: ReactNode }) {
  return (
    <div className={cn('ga-par absolute', className)} style={{ '--d': depth } as CSSProperties}>
      {children}
    </div>
  );
}

export function GPUHeroSection({ totalGPUs, totalRegions, lowestPrice }: GPUHeroSectionProps) {
  const heroRef = useRef<HTMLElement>(null);
  usePointerParallax(heroRef);

  const stats = [
    { icon: IconCpu, value: String(totalGPUs), label: 'GPU models', hue: CYAN },
    { icon: IconWorld, value: String(totalRegions), label: 'Regions', hue: 'var(--accent)' },
    { icon: IconBolt, value: formatCurrency(lowestPrice), label: 'From / hour', hue: 'var(--primary)' },
  ];

  return (
    <section ref={heroRef} className="ga-space relative isolate overflow-hidden border-b border-border/60">
      <div aria-hidden className="starfield absolute inset-0 -z-10 opacity-60" />
      <div aria-hidden className="ga-pixel-grid absolute inset-0 -z-10" />
      <div aria-hidden className="scanlines absolute inset-0 -z-10 opacity-40" />

      <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-6 px-4 pb-10 pt-10 sm:px-8 lg:grid-cols-12 lg:gap-4 lg:px-12 lg:pb-14 lg:pt-12">
        {/* Copy */}
        <div className="relative z-10 lg:col-span-6">
          <div
            className="ga-enter inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-primary"
            style={{ '--i': 0 } as CSSProperties}
          >
            <span className="power-pulse h-2 w-2 rounded-[2px] bg-primary shadow-[0_0_8px_var(--primary)]" />
            The GPU Arcade
          </div>

          <h1
            className="ga-enter mt-5 font-heading text-[3.6rem] font-bold uppercase leading-[0.86] tracking-[-0.03em] text-foreground sm:text-7xl lg:text-[6.2rem] xl:text-[7.4rem]"
            style={{ '--i': 1 } as CSSProperties}
          >
            Pick your
            <br />
            <span className="ga-headline-glow">power.</span>
          </h1>

          <p
            className="ga-enter mt-5 font-heading text-xl font-semibold text-foreground sm:text-2xl"
            style={{ '--i': 2 } as CSSProperties}
          >
            Your next cloud PC is one power-up away.
          </p>
          <p
            className="ga-enter mt-3 max-w-xl text-base leading-relaxed text-muted-foreground"
            style={{ '--i': 3 } as CSSProperties}
          >
            Rent a full Windows machine with an NVIDIA GPU for gaming, creative work, AI and heavy compute.
            Start-up takes a few minutes. Usage is metered by the started minute at the GPU&apos;s hourly rate
            and charged from your wallet balance.
          </p>

          <div className="ga-enter mt-7 flex flex-wrap items-center gap-4" style={{ '--i': 4 } as CSSProperties}>
            <Button asChild size="lg" className="arcade-press h-13 rounded-xl px-7 text-base font-bold">
              <a href="#loadout">
                Explore GPUs
                <IconArrowDown className="size-5" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="arcade-press-accent h-13 rounded-xl border-accent/60 bg-accent/15 px-6 text-base font-semibold text-foreground hover:bg-accent/25"
            >
              <a href="#power-ups">How it works</a>
            </Button>
          </div>
          <Link
            href={machineCreateHref()}
            className="ga-enter mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            style={{ '--i': 5 } as CSSProperties}
          >
            Know what you want? Skip to the configurator
            <IconArrowRight className="size-4" />
          </Link>

          {/* Stats as a compact row on small screens. */}
          <dl className="mt-7 grid grid-cols-3 gap-2 lg:hidden">
            {stats.map((stat) => (
              <div key={stat.label} style={{ '--hue': stat.hue } as CSSProperties} className="ga-hud rounded-xl px-3 py-2.5">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{stat.label}</dt>
                <dd className="mt-0.5 font-heading text-lg font-bold tabular-nums text-foreground">{stat.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Stage */}
        <div aria-hidden className="relative h-[340px] sm:h-[420px] lg:col-span-6 lg:h-[540px]">
          {/* POWER UP lettering */}
          <Layer depth={-8} className="inset-0 flex select-none flex-col items-center justify-center">
            <span className="ga-outline font-heading text-[5.5rem] font-black uppercase leading-[0.82] tracking-tight sm:text-[8rem] lg:text-[10.5rem]">
              Power
            </span>
            <span className="ga-outline font-heading text-[5.5rem] font-black uppercase leading-[0.82] tracking-tight sm:text-[8rem] lg:text-[10.5rem]">
              Up
            </span>
          </Layer>

          <RisingPixels pixels={PIXELS} />

          {/* Volt, back left */}
          <Layer depth={10} className="bottom-[20%] left-[2%] w-[22%]">
            <div className="bob-b relative z-10">
              <GpuCharacter persona={volt} uid="arcade-hero-volt" className="drop-shadow-[0_14px_20px_rgba(0,0,0,0.45)]" />
            </div>
            <Platform hue="var(--primary)" className="-mt-[6%] h-6 w-full sm:h-8" />
          </Layer>

          {/* Nova, hovering top right */}
          <Layer depth={18} className="right-[6%] top-[2%] w-[20%]">
            <div className="bob-a relative z-10">
              <GpuCharacter persona={nova} uid="arcade-hero-nova" className="drop-shadow-[0_14px_20px_rgba(0,0,0,0.45)]" />
            </div>
            <div className="select-aura mx-auto mt-1 h-4 w-3/4 rounded-[50%] [--hue:var(--accent)]" />
          </Layer>

          {/* Titan, centre stage */}
          <Layer depth={6} className="bottom-[4%] left-[28%] w-[40%]">
            <div className="bob-c relative z-10">
              <div className="select-aura absolute -inset-[18%] -z-10 rounded-full blur-2xl [--hue:var(--chart-5)]" />
              <GpuCharacter persona={titan} uid="arcade-hero-titan" className="drop-shadow-[0_20px_30px_rgba(0,0,0,0.5)]" />
            </div>
            <Platform hue="var(--chart-5)" className="-mt-[5%] h-9 w-full sm:h-12" />
          </Layer>

          {/* Oracle, front right */}
          <Layer depth={14} className="bottom-[10%] right-[2%] w-[22%]">
            <div className="bob-a relative z-10">
              <GpuCharacter persona={oracle} uid="arcade-hero-oracle" className="drop-shadow-[0_14px_20px_rgba(0,0,0,0.45)]" />
            </div>
            <Platform hue="var(--chart-4)" className="-mt-[6%] h-6 w-full sm:h-8" />
          </Layer>

          {/* Floating GPU chips */}
          <Layer depth={24} className="left-[8%] top-[10%] w-10 sm:w-12">
            <div className="bob-a rotate-[-12deg]">
              <GpuChip hue={CYAN} />
            </div>
          </Layer>
          <Layer depth={-14} className="left-[58%] top-[6%] w-8 sm:w-10">
            <div className="bob-b rotate-[14deg]">
              <GpuChip hue="var(--primary)" />
            </div>
          </Layer>
          <Layer depth={20} className="bottom-[46%] right-[30%] w-7 sm:w-9">
            <div className="bob-c rotate-[8deg]">
              <GpuChip hue="var(--chart-4)" />
            </div>
          </Layer>
          <Layer depth={-18} className="bottom-[2%] left-[4%] w-9 sm:w-11">
            <div className="bob-b rotate-[-6deg]">
              <GpuChip hue="var(--chart-5)" />
            </div>
          </Layer>

          {/* Stats as floating HUD chips on large screens */}
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const place = ['left-[0%] top-[34%]', 'right-[0%] top-[40%]', 'left-[20%] top-[3%]'][index];
            return (
              <Layer key={stat.label} depth={index % 2 ? -10 : 12} className={cn('z-20 hidden lg:block', place)}>
                <div
                  style={{ '--hue': stat.hue } as CSSProperties}
                  className="ga-hud flex items-center gap-2.5 rounded-xl py-2 pl-2 pr-4"
                >
                  <span className="grid size-8 place-items-center rounded-lg bg-[var(--hue)] text-background shadow-[0_0_14px_var(--hue)]">
                    <Icon className="size-4" />
                  </span>
                  <span>
                    <span className="block font-heading text-lg font-bold leading-none tabular-nums text-foreground">
                      {stat.value}
                    </span>
                    <span className="mt-1 block font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {stat.label}
                    </span>
                  </span>
                </div>
              </Layer>
            );
          })}
        </div>
      </div>
    </section>
  );
}
